import React, { useRef, useState } from 'react';
import { ChevronDown, Loader2, Search } from 'lucide-react';
import { PortalMenu } from '../../components/PortalMenu';
import { ClaimStatePill, totalClaimable } from './queueParts';
import type { ClaimOption } from './types';

interface ClaimSplitButtonProps {
  baseLabel: string;
  options: ClaimOption[];
  claiming: boolean;
  onClaim: (option: ClaimOption | null) => void;
}

export function ClaimSplitButton({ baseLabel, options, claiming, onClaim }: ClaimSplitButtonProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const claimable = totalClaimable(options);
  const allMaxReached = options.length > 0 && options.every((option) => option.maxReached);

  const toggleMenu = () => {
    setQuery('');
    setOpen((value) => !value);
  };
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matchingOptions = options.filter((option) => {
    const text = `${option.label} ${option.taskTypeId ?? ''} ${option.domainId ?? ''}`.toLowerCase();
    return tokens.every((token) => text.includes(token));
  });

  return (
    <div ref={containerRef} className="relative inline-flex rounded-md shadow-sm">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggleMenu}
        disabled={claimable === 0 || claiming}
        title="Choose a task type to claim"
        className="inline-flex items-center gap-2 rounded-l-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {claiming && <Loader2 className="h-4 w-4 animate-spin" />}
        <span>{claiming ? 'Claiming...' : baseLabel}</span>
        {allMaxReached && !claiming && <ClaimStatePill label="Max reached" />}
      </button>
      <button
        type="button"
        aria-label="Choose task type to claim"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={claiming || options.length === 0}
        onClick={toggleMenu}
        className="inline-flex items-center justify-center rounded-r-md bg-primary px-2 py-2 text-white border-l border-primary-hover/40 hover:bg-primary-hover disabled:opacity-50"
      >
        <ChevronDown className="h-4 w-4" />
      </button>
      {open && (
        <PortalMenu
          anchorRef={containerRef}
          onRequestClose={() => setOpen(false)}
          className="w-max min-w-[min(24rem,calc(100vw-8rem))] !max-w-[calc(100vw-8rem)]"
        >
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search task types"
                aria-label="Search task types"
                className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm text-slate-900 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          <div role="menu" aria-label="Task types to claim" className="max-h-72 overflow-y-auto py-1">
            {matchingOptions.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-500">No matching task types</p>
            )}
            {matchingOptions.map((option) => (
              <button
                key={`${option.taskTypeId ?? '__any__'}::${option.domainId ?? ''}`}
                type="button"
                role="menuitem"
                disabled={option.claimingPaused || option.maxReached || option.availableCount === 0}
                onClick={() => {
                  setOpen(false);
                  onClaim(option);
                }}
                className="w-full flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <span className="min-w-0 flex-1 whitespace-normal break-words">{option.label}</span>
                <span className="flex shrink-0 flex-wrap items-center gap-2 whitespace-nowrap text-xs text-gray-500">
                  {option.claimingPaused && (
                    <ClaimStatePill label="Paused" />
                  )}
                  {option.maxReached && (
                    <ClaimStatePill label="Max reached" />
                  )}
                  <span>{option.availableCount} available</span>
                </span>
              </button>
            ))}
          </div>
        </PortalMenu>
      )}
    </div>
  );
}
